/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useEffect, useRef, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import { logger } from '../utils/logger';
import { useNotification } from '../hooks/useNotification';

// Service Worker communication helper
const sendMessageToServiceWorker = (type: string, data: unknown) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type, data });
    logger.log('PaymentContext: Message sent to service worker:', type, data);
  } else {
    logger.warn('PaymentContext: Service worker not available for background timer');
  }
};

interface PaymentContextType {
  timeLeft: number;
  paymentData: {
    amount: number;
    qrCode: string;
    type: 'checkout' | 'topup';
  } | null;
  setPaymentData: (
    data: {
      amount: number;
      qrCode: string;
      type: 'checkout' | 'topup';
    } | null,
  ) => void;
  clearPaymentData: () => void;
  globalPaymentModal: {
    isOpen: boolean;
    amount: number;
    paymentQrCode: string;
    orderId: string;
    onPaymentSuccess?: () => void;
    showAPlusWaitScreen?: boolean;
    aPlusPaymentData?: {
      pushNotiLink?: string;
      deepLink?: string;
      orderId?: string;
    } | null;
    onCancelAPlusPayment?: () => void;
  };
  setGlobalPaymentModal: (modal: {
    isOpen: boolean;
    amount: number;
    paymentQrCode: string;
    orderId: string;
    onPaymentSuccess?: () => void;
    showAPlusWaitScreen?: boolean;
    aPlusPaymentData?: {
      pushNotiLink?: string;
      deepLink?: string;
      orderId?: string;
    } | null;
    onCancelAPlusPayment?: () => void;
  }) => void;
}

export const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timeLeft, setTimeLeftState] = useState(300); // 5 minutes
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { showPaymentExpiredNotification } = useNotification();

  // Debug: Log timeLeft changes
  useEffect(() => {
    logger.log('PaymentContext: timeLeft changed to:', timeLeft);
  }, [timeLeft]);

  const [paymentData, setPaymentDataState] = useState<{
    amount: number;
    qrCode: string;
    type: 'checkout' | 'topup';
  } | null>(null);

  const setPaymentData = useCallback(
    (
      data: {
        amount: number;
        qrCode: string;
        type: 'checkout' | 'topup';
      } | null,
    ) => {
      logger.log('PaymentContext: setPaymentData called with:', data);
      setPaymentDataState(data);

      if (data) {
        logger.log('PaymentContext: Starting countdown timer');
        // Reset timer when new payment data is set
        setTimeLeftState(300); // 5 minutes

        // Generate unique order ID for this payment session
        const orderId = `payment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Start background timer in service worker
        sendMessageToServiceWorker('START_PAYMENT_TIMER', {
          orderId: orderId,
          timeLeft: 300,
          amount: data.amount,
        });

        // Clear existing timer
        if (countdownTimerRef.current) {
          logger.log('PaymentContext: Clearing existing timer');
          clearInterval(countdownTimerRef.current);
        }

        // Start new countdown timer
        countdownTimerRef.current = setInterval(() => {
          setTimeLeftState((prev) => {
            logger.log('PaymentContext: Timer tick, prev:', prev);
            const newTime = prev <= 1 ? 0 : prev - 1;

            if (prev <= 1) {
              logger.log('PaymentContext: Timer expired, clearing everything');
              // Timer expired, clear everything
              if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
                countdownTimerRef.current = null;
              }

              // Stop background timer in service worker
              sendMessageToServiceWorker('STOP_PAYMENT_TIMER', { orderId });

              setPaymentDataState(null);
              // Show system notification for QR code expiration (fallback for active tab)
              showPaymentExpiredNotification().catch((error) => {
                logger.error('Failed to show payment expired notification:', error);
              });
              return 0;
            }
            return newTime;
          });
        }, 1000);
        logger.log('PaymentContext: Timer started with ID:', countdownTimerRef.current);
      } else {
        logger.log('PaymentContext: Clearing timer and resetting state');
        // Clear timer when payment data is cleared
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
        setTimeLeftState(300);
      }
    },
    [showPaymentExpiredNotification],
  );

  // Function to clear payment data and reset timer
  const clearPaymentData = useCallback(() => {
    logger.log('PaymentContext: clearPaymentData called');
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setPaymentDataState(null);
    setTimeLeftState(300);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);
  const [globalPaymentModal, setGlobalPaymentModal] = useState<{
    isOpen: boolean;
    amount: number;
    paymentQrCode: string;
    orderId: string;
    onPaymentSuccess?: () => void;
    showAPlusWaitScreen?: boolean;
    aPlusPaymentData?: {
      pushNotiLink?: string;
      deepLink?: string;
      orderId?: string;
    } | null;
    onCancelAPlusPayment?: () => void;
  }>({
    isOpen: false,
    amount: 0,
    paymentQrCode: '',
    orderId: '',
    onPaymentSuccess: undefined,
    showAPlusWaitScreen: false,
    aPlusPaymentData: null,
    onCancelAPlusPayment: undefined,
  });

  return (
    <PaymentContext.Provider
      value={{
        timeLeft,
        paymentData,
        setPaymentData,
        clearPaymentData,
        globalPaymentModal,
        setGlobalPaymentModal,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
